import {RunInAction} from "mobx-graphlink";
import {O} from "web-vcore";
import * as THREE from "three";

export type ObjRenderStats = {name: string; elapsedMs: number; drawCallCount: number};

class MapObjectRenderStatsStore {
	/** Per-map-object aggregated render-time stats (updated every ~30 frames). Key = MapObject.id */
	@O.ref accessor objStats = new Map<string, ObjRenderStats>();

	/** Snapshot of renderer.info from the most recent rendered frame. */
	@O accessor rendererInfo: {
		calls: number; triangles: number; points: number; lines: number;
		geometries: number; textures: number; programs: number;
	} | null = null;
	/** EMA-smoothed CPU render time (ms) for the Three.js render call, updated every frame. */
	@O accessor frameRenderMs: number | null = null;
	/** EMA-smoothed total frame interval (ms), i.e. rAF-to-rAF time. Includes render + all other per-frame work. */
	@O accessor totalFrameMs: number | null = null;
}

export const mapRenderStats = new MapObjectRenderStatsStore();

// ── Per-object timing state ──────────────────────────────────────────────────

type TimingEntry = {startTime: number; elapsed: number; objId: string; origBefore: THREE.Object3D["onBeforeRender"]; origAfter: THREE.Object3D["onAfterRender"]};

const timingData = new WeakMap<THREE.Object3D, TimingEntry>();
const trackedObjects = new Set<THREE.Object3D>();

/** childadded event listener stored per-node for cleanup. */
const childListeners = new WeakMap<THREE.Object3D, (e: {child: THREE.Object3D})=>void>();

/** Per-root registry of attached nodes — enables cleanup without traversal (safe after R3F detaches children). */
type RootAttached = {renderables: Set<THREE.Object3D>; listenerNodes: Set<THREE.Object3D>};
const rootAttached = new WeakMap<THREE.Object3D, RootAttached>();

function isRenderable(obj: THREE.Object3D): obj is THREE.Mesh | THREE.Line | THREE.Points {
	return obj instanceof THREE.Mesh || obj instanceof THREE.Line || obj instanceof THREE.Points;
}

/**
 * Attach timing hooks to all renderables in `subtree`, AND set up a `childadded`
 * listener on every node in `subtree` so future additions are auto-tracked.
 * `root` is the original root passed to `attachRenderTiming` — used to register
 * newly discovered nodes so `detachRenderTiming` can clean them up without traversal.
 */
function attachToSubtree(subtree: THREE.Object3D, objId: string, root: THREE.Object3D) {
	const attached = rootAttached.get(root)!;
	subtree.traverse(node=>{
		// ── Set up childadded listener so future children are auto-tracked ──
		const existingListener = childListeners.get(node);
		if (existingListener) node.removeEventListener("childadded", existingListener as any);
		const handler = (e: {child: THREE.Object3D})=>{
			attachToSubtree(e.child, objId, root);
		};
		node.addEventListener("childadded", handler as any);
		childListeners.set(node, handler);
		attached.listenerNodes.add(node);

		// ── Attach timing to this node if it is renderable ──
		if (!isRenderable(node)) return;
		if (timingData.has(node)) detachTimingFromSingleObject(node); // clean up before re-attaching
		const entry: TimingEntry = {
			startTime: 0, elapsed: 0, objId,
			origBefore: node.onBeforeRender,
			origAfter: node.onAfterRender,
		};
		timingData.set(node, entry);
		trackedObjects.add(node);
		attached.renderables.add(node);
		node.onBeforeRender = function(...args) {
			entry.origBefore.apply(this, args);
			entry.startTime = performance.now();
		};
		node.onAfterRender = function(...args) {
			entry.origAfter.apply(this, args);
			entry.elapsed = performance.now() - entry.startTime;
		};
	});
}

function detachTimingFromSingleObject(obj: THREE.Object3D) {
	const entry = timingData.get(obj);
	if (!entry) return;
	obj.onBeforeRender = entry.origBefore;
	obj.onAfterRender = entry.origAfter;
	timingData.delete(obj);
	trackedObjects.delete(obj);
}

/**
 * Attach render-timing to `root` and all its current + future descendants.
 * Uses Three.js `childadded` events to auto-detect new children — no manual
 * dependency array needed.
 */
export function attachRenderTiming(root: THREE.Object3D, objId: string) {
	detachRenderTiming(root);
	rootAttached.set(root, {renderables: new Set(), listenerNodes: new Set()});
	attachToSubtree(root, objId, root);
}

/**
 * Remove all timing hooks and childadded listeners for `root`.
 * Iterates the registry set directly — safe even after R3F has already
 * detached the Three.js children from the group before cleanup runs.
 */
export function detachRenderTiming(root: THREE.Object3D) {
	const attached = rootAttached.get(root);
	if (!attached) return;
	for (const obj of attached.renderables) detachTimingFromSingleObject(obj);
	for (const node of attached.listenerNodes) {
		const handler = childListeners.get(node);
		if (handler) {
			node.removeEventListener("childadded", handler as any);
			childListeners.delete(node);
		}
	}
	rootAttached.delete(root);
}

// ── Per-frame stats collection ───────────────────────────────────────────────

const EMA = 0.1; // exponential moving average factor (~10-frame smoothing window)
let renderStartTime = 0;
let lastCollectTime = 0;
let frameCount = 0;

/** Called from scene.onBeforeRender — marks when the Three.js render call begins. */
export function markRenderStart() {
	renderStartTime = performance.now();
}

/** Called from scene.onAfterRender — records how long the render call took. */
export function markRenderEnd() {
	const rawMs = performance.now() - renderStartTime;
	RunInAction("markRenderEnd", ()=>{
		mapRenderStats.frameRenderMs = mapRenderStats.frameRenderMs == null
			? rawMs
			: mapRenderStats.frameRenderMs * (1 - EMA) + rawMs * EMA;
	});
}

/**
 * Call this each frame (after render) to collect renderer.info and aggregate per-object timings.
 * `getObjName` maps a MapObject id to its display name.
 */
export function collectRenderStats(gl: THREE.WebGLRenderer, getObjName: (id: string)=>string) {
	// Total frame interval (rAF-to-rAF), measured between successive collectRenderStats calls
	const now = performance.now();
	if (lastCollectTime > 0) {
		const rawInterval = now - lastCollectTime;
		RunInAction("collectRenderStats.totalFrameMs", ()=>{
			mapRenderStats.totalFrameMs = mapRenderStats.totalFrameMs == null
				? rawInterval
				: mapRenderStats.totalFrameMs * (1 - EMA) + rawInterval * EMA;
		});
	}
	lastCollectTime = now;

	// Always update renderer info (read from the frame that just completed)
	RunInAction("collectRenderStats.rendererInfo", ()=>{
		mapRenderStats.rendererInfo = {
			calls: gl.info.render.calls,
			triangles: gl.info.render.triangles,
			points: gl.info.render.points,
			lines: gl.info.render.lines,
			geometries: gl.info.memory.geometries,
			textures: gl.info.memory.textures,
			programs: gl.info.programs?.length ?? 0,
		};
	});

	// Aggregate per-object timings every 30 frames (~0.5 s at 60 fps)
	frameCount++;
	if (frameCount % 30 !== 0) return;

	const agg = new Map<string, {elapsed: number; calls: number}>();
	for (const obj of trackedObjects) {
		const data = timingData.get(obj);
		if (!data) continue;
		const prev = agg.get(data.objId) ?? {elapsed: 0, calls: 0};
		prev.elapsed += data.elapsed;
		prev.calls++;
		agg.set(data.objId, prev);
	}

	RunInAction("collectRenderStats.objStats", ()=>{
		mapRenderStats.objStats = new Map(
			[...agg.entries()].map(([id, {elapsed, calls}])=>[
				id,
				{name: getObjName(id), elapsedMs: elapsed, drawCallCount: calls} as ObjRenderStats,
			])
		);
	});
}
